import { useEffect, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Box, Group, Stack, Text } from '@mantine/core';
import { ArrowRight, Settings } from 'lucide-react';
import { SurfaceCard } from '@/design/SurfaceCard';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { ClassificationSurfaceCardViewModel } from './ClassificationSurfaceCardViewModel';
import { categoryNoun } from './ClassificationOptions';

interface ClassificationSurfaceCardProps {
    category: string;
    variant?: 'standard' | 'compact';
}

/** Muted-gray accent (theme tokens) matching the topic card token shape, minus the data tints. */
const GRAY_TOKENS = {
    solid: 'var(--bm-color-accent-neutral)',
    soft: 'var(--bm-color-accent-neutral-soft)',
    border: 'var(--bm-color-accent-neutral-border)',
    text: 'var(--bm-color-accent-neutral-text)',
};

/**
 * Entry-point card opening the classification builder scoped to a surface's
 * category. Mirrors the topic card chrome (accent rail, icon box, Explore
 * footer) but with a muted gray accent so it reads as a setup affordance.
 */
export default function ClassificationSurfaceCard({ category, variant = 'standard' }: ClassificationSurfaceCardProps): ReactElement {
    const vm = useViewModel(ClassificationSurfaceCardViewModel);
    const noun = categoryNoun(category);

    useEffect(() => {
        void vm.initialize(category);
    }, [vm, category]);

    const countLabel = `${vm.count} ${(vm.count === 1 ? noun.singular : noun.plural).toLowerCase()}`;

    const countBadge = vm.loaded ? (
        <Badge variant="outline" color="gray" radius="sm">
            {countLabel}
        </Badge>
    ) : null;

    const iconBox = (size: number, icon: number): ReactElement => (
        <Box
            style={{
                width: size,
                height: size,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: GRAY_TOKENS.soft,
                border: `1px solid ${GRAY_TOKENS.border}`,
                borderRadius: 'var(--bm-radius)',
            }}
        >
            <Settings size={icon} color={GRAY_TOKENS.solid} />
        </Box>
    );

    if (variant === 'compact') {
        return (
            <Link to={`/classification/${category.toLowerCase()}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}>
                <SurfaceCard $padding="md" style={{ height: '100%', justifyContent: 'center', borderLeft: `2px solid ${GRAY_TOKENS.solid}` }}>
                    <Group gap="md" wrap="nowrap" align="center">
                        {iconBox(32, 16)}
                        <Box style={{ flex: 1, minWidth: 0 }}>
                            <Text fw={700} truncate style={{ fontSize: '14px', letterSpacing: '-0.02em' }}>
                                {noun.plural}
                            </Text>
                            <Text size="xs" c="dimmed" truncate mt={4}>
                                Define and score {category} signals across sessions.
                            </Text>
                        </Box>
                        <Group gap="sm" wrap="nowrap" align="center" style={{ flexShrink: 0 }}>
                            {countBadge}
                            <ArrowRight size={14} color="var(--bm-color-text-dimmed)" />
                        </Group>
                    </Group>
                </SurfaceCard>
            </Link>
        );
    }

    return (
        <Link to={`/classification/${category.toLowerCase()}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}>
            <SurfaceCard $padding="md" style={{ height: '100%', borderTop: `2px solid ${GRAY_TOKENS.solid}` }}>
                <Group justify="space-between" align="center" mb="sm">
                    <Group gap="sm">
                        {iconBox(30, 15)}
                        <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ fontSize: '9px', letterSpacing: '0.1em' }}>
                            {category}
                        </Text>
                    </Group>
                </Group>

                <Stack gap="sm" style={{ flex: 1 }} justify="space-between">
                    <Box>
                        <Text fw={700} style={{ fontSize: '16px', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
                            {noun.plural}
                        </Text>
                        <Text size="xs" c="dimmed" mt={6} lh={1.5}>
                            Define and score {category} signals across sessions.
                        </Text>
                    </Box>
                    <Group justify="space-between" align="center" mt="md">
                        {countBadge}
                        <Group gap={4} align="center" wrap="nowrap" style={{ color: GRAY_TOKENS.text }}>
                            <Text size="xs" fw={700} style={{ fontSize: '11px' }}>
                                Configure
                            </Text>
                            <ArrowRight size={13} />
                        </Group>
                    </Group>
                </Stack>
            </SurfaceCard>
        </Link>
    );
}
